import {
  clearAuthTokens,
  type IAuthTokens,
  setAuthTokens,
  type TokenRefreshRequest,
} from 'axios-jwt';
import axios from 'axios';
import { BASE_URL, REDIRECT_URI } from '@/Constants';
import CoreApi from '@/api/CoreApi';

/**
 * Fetches user access token.
 * @param authCode The user's authorization code given by Cognito.
 */
export async function login(authCode: string): Promise<void> {
  try {
    const response = await axios.get(`${BASE_URL}/login/token/${authCode}`, {
      params: { redirect_uri: REDIRECT_URI },
    });
    const tokens: IAuthTokens = {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
    };
    await setAuthTokens(tokens);
  } catch {
    await clearAuthTokens();
    await CoreApi.authStore.logoutAndClearUserData();
    throw new Error('Login token exchange failed');
  }
}

/**
 * Function for refreshing the token.
 * @param refreshToken The refresh token.
 * @returns A promise that resolves with the new auth tokens
 */
export const requestRefresh: TokenRefreshRequest = async (
  requestRefreshToken: string,
): Promise<IAuthTokens> => {
  try {
    // Important this has to be a separate instance of CoreApi.axiosClient to avoid an infinite loop
    const response = await axios.post(`${BASE_URL}/login/refresh-token`, {
      refresh_token: requestRefreshToken,
    });
    const tokens: IAuthTokens = {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
    };
    await setAuthTokens(tokens);
    return tokens;
  } catch {
    await clearAuthTokens();
    await CoreApi.authStore.logoutAndClearUserData();
    throw new Error('Failed to refresh tokens.');
  }
};
